import React, { useState, memo } from 'react';
import {
  Box,
  Typography,
  CardContent,
  Avatar,
  Chip,
  Button,
  IconButton,
  Stack,
  Divider,
  Collapse,
  Grid,
  Badge,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  AttachMoney,
  Payment,
  ExpandMore,
  ExpandLess,
  Email,
  AccountBalance,
  MovieFilter,
  Instagram,
  YouTube,
  Language,
  FolderOpen,
  CheckCircleOutline,
  Schedule,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { GradientCard } from './gradientCard';
import { getApplicantDisplayName } from './castingCallHelpers';

const ApplicantCard = memo(({ 
  applicant, 
  castingCall, 
  onAccept, 
  onReject, 
  onAssignPayout, 
  onIssuePayment, 
  onAssignOrganizer,
  onOpenChat 
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  
  const isSelected = castingCall.call_type === 'creator'
    ? castingCall.selected_creator_id === applicant.username
    : castingCall.selected_user_id === applicant.id;

  const payoutAmount = applicant.assigned_payout || 0;
  const isPaymentIssued = applicant.payment_status === 'issued';
  const isPaymentCompleted = applicant.payment_status === 'completed';
  const displayName = getApplicantDisplayName(applicant, castingCall);

  return (
    <GradientCard sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              isSelected ? <CheckCircle sx={{ color: 'success.main', fontSize: 24 }} /> : null
            }
          >
            <Avatar
              src={applicant.pfphref}
              sx={{ 
                width: 64, 
                height: 64, 
                border: `3px solid ${isSelected ? theme.palette.success.main : 'transparent'}` 
              }}
            >
              {displayName[0]}
            </Avatar>
          </Badge>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {displayName}
                </Typography>
                
                {castingCall.call_type === 'creator' && applicant.username && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    @{applicant.username}
                  </Typography>
                )}
                {castingCall.call_type !== 'creator' && applicant.email && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {applicant.email}
                  </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {applicant.user_type && (
                    <Chip label={applicant.user_type} size="small" variant="outlined" />
                  )}
                  {isSelected && <Chip label="Selected" size="small" color="success" />}
                  {payoutAmount > 0 && (
                    <Chip
                      label={`$${payoutAmount.toFixed(2)} assigned`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {isPaymentCompleted && (
                    <Chip label="Paid" size="small" color="success" icon={<CheckCircleOutline />} />
                  )}
                  {isPaymentIssued && !isPaymentCompleted && (
                    <Chip label="Payment Issued" size="small" color="warning" icon={<Schedule />} />
                  )}
                </Stack>

                {applicant.application_message && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 1.5, 
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: 1,
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                      Application Message:
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      "{applicant.application_message}"
                    </Typography>
                  </Box>
                )}
              </Box>

              <IconButton onClick={() => setExpanded(!expanded)} size="small">
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            {castingCall.call_type === 'creator' && applicant.social_stats && (
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {applicant.social_stats.tiktok_followers > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MovieFilter fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {applicant.social_stats.tiktok_followers.toLocaleString()} TikTok
                    </Typography>
                  </Box>
                )}
                {applicant.social_stats.instagram_followers > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Instagram fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {applicant.social_stats.instagram_followers.toLocaleString()} IG
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}

            {!isSelected ? (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircle />}
                  onClick={() => onAccept(applicant)}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  }}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Cancel />}
                  onClick={() => onReject(applicant)}
                  color="error"
                >
                  Reject
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ChatBubbleOutline />}
                  onClick={() => onOpenChat(applicant)}
                  color="primary"
                >
                  Chat
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AttachMoney />}
                  onClick={() => onAssignPayout(applicant)}
                >
                  {payoutAmount > 0 ? 'Update Payout' : 'Assign Payout'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FolderOpen />}
                  onClick={() => onAssignOrganizer(applicant)}
                  color="secondary"
                >
                  Assign Organizers
                </Button>
                {payoutAmount > 0 && !isPaymentCompleted && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Payment />}
                    onClick={() => onIssuePayment(applicant)}
                    disabled={isPaymentIssued}
                  >
                    {isPaymentIssued ? 'Payment Issued' : 'Issue Payment'}
                  </Button>
                )}
              </Stack>
            )}

            <Collapse in={expanded}>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {applicant.email && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{applicant.email}</Typography>
                    </Box>
                  </Grid>
                )}
                {applicant.company_name && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalance fontSize="small" color="action" />
                      <Typography variant="body2">{applicant.company_name}</Typography>
                    </Box>
                  </Grid>
                )}
                
                {castingCall.call_type === 'creator' && applicant.links && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Social Profiles
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {applicant.links.tiktok && (
                        <Chip
                          label="TikTok"
                          size="small"
                          component="a"
                          href={applicant.links.tiktok}
                          target="_blank"
                          clickable
                          icon={<Language />}
                        />
                      )}
                      {applicant.links.instagram && (
                        <Chip
                          label="Instagram"
                          size="small"
                          component="a"
                          href={applicant.links.instagram}
                          target="_blank"
                          clickable
                          icon={<Instagram />}
                        />
                      )}
                      {applicant.links.youtube && (
                        <Chip
                          label="YouTube"
                          size="small"
                          component="a"
                          href={applicant.links.youtube}
                          target="_blank"
                          clickable
                          icon={<YouTube />}
                        />
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Collapse>
          </Box>
        </Box>
      </CardContent>
    </GradientCard>
  );
});

ApplicantCard.displayName = 'ApplicantCard';

export default ApplicantCard;